import { useEffect, useMemo, useState } from 'react';

import { Progress } from 'antd';
import type { TFunction } from 'i18next';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PATHS } from '@/config/paths';
import { useFloodJobs } from '@/features/analytics/context/FloodJobsContext';
import { getAnalysisKindMeta } from '@/features/map/utils/analysisKindMeta';
import {
  computeJobEtaMs,
  getJobDisplayProgressPercent,
  getRunningStepIndex,
} from '@/mocks/floodJobs';

export interface MapSarJobProgressOverlayProps {
  leftOffsetPx: number;
  bottomPx: string;
  zIndex: number;
}

function formatJobEtaHuman(ms: number, t: TFunction): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  if (totalSec < 60) return t('ops.jobs.mapProgress.etaHumanSeconds', { n: totalSec });

  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (s === 0) return t('ops.jobs.mapProgress.etaHumanMinutesOnly', { m });
    return t('ops.jobs.mapProgress.etaHumanMinutesSeconds', { m, s });
  }

  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (m === 0) return t('ops.jobs.mapProgress.etaHumanHoursOnly', { h });
  return t('ops.jobs.mapProgress.etaHumanHoursMinutes', { h, m });
}

/**
 * Floating progress card while a map-submitted SAR job is running (tracks `activeSarJobId`).
 * ETA đếm ngược mỗi giây (`nowMs`); % tiến độ mượt theo `targetDurationMs`, không lùi hơn % server.
 */
export function MapSarJobProgressOverlay({
  leftOffsetPx,
  bottomPx,
  zIndex,
}: MapSarJobProgressOverlayProps) {
  const { t } = useTranslation();
  const { jobs, activeSarJobId } = useFloodJobs();

  const job = useMemo(
    () => (activeSarJobId ? jobs.find((j) => j.id === activeSarJobId) : undefined),
    [jobs, activeSarJobId],
  );

  // Tick mỗi giây để đếm ngược ETA (tách khỏi engine jobs để không kéo theo re-render toàn cục).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!job || job.status !== 'running') return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [job]);

  if (!job || (job.status !== 'running' && job.status !== 'queued')) return null;

  const kindMeta = getAnalysisKindMeta(job.parameters.analysisKind);
  const displayProgress = getJobDisplayProgressPercent(job, nowMs);
  const etaMs = job.runtime ? computeJobEtaMs(job, nowMs) : 0;
  const isFinalizing = displayProgress >= 90;
  const runningStepIndex =
    job.runtime && job.status === 'running'
      ? getRunningStepIndex(job.runtime.stageWeights, displayProgress)
      : job.steps.findIndex((s) => s.status === 'running');
  const runningStep =
    runningStepIndex >= 0
      ? job.steps[runningStepIndex]
      : job.steps.find((s) => s.status === 'running');

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto rounded-xl border bg-[var(--map-chrome-panel)] shadow-[0_8px_28px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:shadow-[0_8px_28px_rgba(0,0,0,0.45)]"
      style={{
        position: 'absolute',
        left: leftOffsetPx,
        bottom: bottomPx,
        zIndex,
        maxWidth: 'min(340px, calc(100vw - 24px))',
        padding: '12px 14px',
        borderColor: `${kindMeta.accentColor}59`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Loader2 size={16} className="animate-spin" color={kindMeta.accentColor} />
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)' }}>
          {t(kindMeta.progressTitleKey)}
        </span>
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-secondary)',
          marginBottom: 6,
          lineHeight: 1.35,
        }}
      >
        {job.name}
      </div>
      {runningStep && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 8,
            fontSize: 11,
            color: 'var(--text-secondary)',
          }}
        >
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {isFinalizing ? (
              t('ops.jobs.mapProgress.finalizing')
            ) : (
              <>
                {t('ops.jobs.mapProgress.stepLine')}{' '}
                {job.runtime && (
                  <span style={{ fontWeight: 700, color: kindMeta.accentColor }}>
                    {t('ops.jobs.mapProgress.etaLine', { eta: formatJobEtaHuman(etaMs, t) })}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
          {t('ops.jobs.colStatusProgress')}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: kindMeta.accentColor,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {displayProgress}%
        </span>
      </div>
      <Progress
        percent={displayProgress}
        showInfo={false}
        strokeColor={kindMeta.accentColor}
        railColor="var(--chart-grid-stroke)"
        size="small"
      />
      <Link
        to={PATHS.OPS_JOBS}
        style={{
          display: 'inline-block',
          marginTop: 10,
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--accent-blue)',
        }}
      >
        {t('ops.jobs.mapProgress.openPipeline')}
      </Link>
    </div>
  );
}
